import { createRoute, lazyRouteComponent, Outlet } from '@tanstack/react-router';
import { DateTime } from 'luxon';
import { z } from 'zod/v3';

import { menuLayoutRoute } from 'root/MenuLayout';
import RequireWarehouse from 'root/RequireWarehouse';

import OpsOptimisedUiEnabledRoute from './OpsOptimisedUiEnabledRoute';

const opsReceivingRootRoute = createRoute({
  getParentRoute: () => menuLayoutRoute,
  id: 'ops-receiving-root',
});

const OpsReceivingRequireWarehouse = () => {
  return (
    <RequireWarehouse
      routeWithWarehouseId="/ops-receiving/warehouse/$warehouseId"
      routeWithoutWarehouseId="/ops-receiving"
    >
      <Outlet />
    </RequireWarehouse>
  );
};

const requireWarehouseRoute = createRoute({
  getParentRoute: () => opsReceivingRootRoute,
  path: '/ops-receiving',
  component: OpsReceivingRequireWarehouse,
});

const opsReceivingEnabledRoute = createRoute({
  getParentRoute: () => requireWarehouseRoute,
  id: 'opsReceivingEnabledRoute',
  component: OpsOptimisedUiEnabledRoute,
});

const opsReceivingWarehouseWrapperRoute = createRoute({
  getParentRoute: () => opsReceivingEnabledRoute,
  path: 'warehouse/$warehouseId',
  parseParams: z.object({ warehouseId: z.string().uuid() }).parse,
});

export const receivingRoute = createRoute({
  getParentRoute: () => opsReceivingWarehouseWrapperRoute,
  path: '/',
  component: lazyRouteComponent(() => import('./ReceivingView/ReceivingView')),
  validateSearch: z.object({
    filterPOsByOutOfStockItems: z.boolean().optional(),
    hideFullyReceivedPOs: z.boolean().optional(),
    inboundIdForShipmentDetails: z.string().optional(),
    inboundLoadId: z.string().optional(),
    locationSearchModalForBatch: z.string().optional(),
    outerCaseId: z.string().optional(),
    productId: z.string().optional(),
    scannedExpirationDate: z.string().optional(),
    scannedGtin: z.string().optional(),
    searchQuery: z.coerce.string().optional(),
    showMultipleProductsWithinOuterCaseModal: z.boolean().optional(),
    showNoPoFoundModal: z.boolean().optional(),
    showNoResultsModal: z.boolean().optional(),
    showOuterCaseMissingProductsModal: z.boolean().optional(),
    showOuterCaseScanningNotEnabledModal: z.boolean().optional(),
    showSearchDrawer: z.boolean().optional(),
    showUserGuides: z.boolean().optional(),
    showWaybillCreationPromptModal: z.boolean().optional(),
  }).parse,
});

const recentlyAddedItemsIndexRoute = createRoute({
  getParentRoute: () => opsReceivingWarehouseWrapperRoute,
  path: 'recently-added-items',
});

const recentlyAddedItemsRoute = createRoute({
  getParentRoute: () => recentlyAddedItemsIndexRoute,
  path: '/',
  component: lazyRouteComponent(() => import('./RecentlyAddedItemsView/RecentlyAddedItemsView')),
  validateSearch: z.object({
    q: z.coerce.string().optional().catch(''),
  }).parse,
});

export const editGoodsReceiptRoute = createRoute({
  getParentRoute: () => recentlyAddedItemsIndexRoute,
  path: '/edit',
  component: lazyRouteComponent(() => import('./EditGoodsReceiptView/EditGoodsReceiptView')),
  validateSearch: z.object({
    searchQuery: z.coerce.string().optional(),
    locationSearchModalForBatch: z.string().optional(),
  }).parse,
});

export const selectInboundLoadRoute = createRoute({
  getParentRoute: () => opsReceivingWarehouseWrapperRoute,
  path: 'select-po/$productId',
  component: lazyRouteComponent(() => import('./SelectInboundLoadView/SelectInboundLoadView')),
  parseParams: (params) => ({ productId: z.string().parse(params.productId) }),
  validateSearch: z.object({
    inboundLoadId: z.string().optional(),
    outerCaseId: z.string().optional(),
    scannedExpirationDate: z.string().optional(),
    showWaybillCreationPromptModal: z.boolean().optional(),
    vendorIds: z.array(z.string()).optional(),
  }).parse,
});

const expirationDateSchema = z
  .string()
  .optional()
  .transform((dateString) => {
    if (!dateString) return undefined;

    const date = DateTime.fromISO(dateString);
    return date.isValid ? dateString : undefined;
  });

export const addToInventoryRoute = createRoute({
  getParentRoute: () => opsReceivingWarehouseWrapperRoute,
  path: '$productId',
  component: lazyRouteComponent(() => import('./AddToInventoryView/AddToInventoryView')),
  parseParams: (params) => ({ productId: z.string().parse(params.productId) }),
  validateSearch: z.object({
    showItemInfoDrawer: z.boolean().optional(),
    inboundLoadId: z.string().optional(),
    prevPath: z.string().optional(),
    locationSearchModalForBatch: z.string().optional(),
    showChangeLog: z.boolean().optional(),
    searchQuery: z.coerce.string().optional(),
    expirationDate: expirationDateSchema.optional(), // used for prefilling expirationDate in the form
    outerCaseId: z.string().optional(),
    showExcessiveQuantityModal: z.boolean().optional(),
    showMissingAssortmentOfferModal: z.boolean().optional(),
    inboundIdForShipmentDetails: z.string().optional(),
  }).parse,
});

export const addWaybillRoute = createRoute({
  getParentRoute: () => opsReceivingWarehouseWrapperRoute,
  path: 'add-waybill/$inboundLoadId',
  component: lazyRouteComponent(() => import('./AddWaybillView/AddWaybillView')),
  parseParams: (params) => ({ inboundLoadId: z.string().parse(params.inboundLoadId) }),
  validateSearch: z.object({
    inboundLoadName: z.string().optional(),
    productId: z.string().optional(),
    outerCaseId: z.string().optional(),
    expirationDate: z.string().optional(),
    prevPath: z.string().optional(),
  }),
});

export const editWaybillRoute = createRoute({
  getParentRoute: () => opsReceivingWarehouseWrapperRoute,
  path: 'edit-waybill/$waybillId',
  component: lazyRouteComponent(() => import('./EditWaybillView')),
  parseParams: (params) => ({ waybillId: z.string().parse(params.waybillId) }),
  validateSearch: z.object({
    inboundLoadName: z.string().optional(),
    inboundLoadId: z.string().optional(),
    productId: z.string().optional(),
    outerCaseId: z.string().optional(),
    expirationDate: z.string().optional(),
  }),
});

export const opsReceivingRouteTree = opsReceivingRootRoute.addChildren({
  requireWarehouseRoute: requireWarehouseRoute.addChildren({
    opsReceivingEnabledRoute: opsReceivingEnabledRoute.addChildren({
      opsReceivingWarehouseWrapperRoute: opsReceivingWarehouseWrapperRoute.addChildren({
        receivingRoute,
        recentlyAddedItemsIndexRoute: recentlyAddedItemsIndexRoute.addChildren({
          recentlyAddedItemsRoute,
          editGoodsReceiptRoute,
        }),
        selectInboundLoadRoute,
        addToInventoryRoute,
        addWaybillRoute,
        editWaybillRoute,
      }),
    }),
  }),
});
